import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  VIDEO_PACK,
  serializeTags,
  serializeKeyMoments,
  type VideoPackField,
} from '../lib/videoPack.ts';
import { scoreMedia, type ScorableMedia } from '../lib/mediaHealth.ts';
import { metaFieldPatch, rowFieldPatch, type DetailMediaItem, type SavePatch } from '../lib/mediaFieldPatch.ts';
import { btnGhostSm } from '../lib/ui.ts';

export type { DetailMediaItem, SavePatch };

/**
 * Visual CMS — the detail MODAL (visual-cms.md §4 / §7 Slice 2). Click a card → a CENTRED
 * dialog over a dimmed backdrop (not a drawer): the media large + filename, the health bar,
 * every field inline-editable (title + alt + caption + the video PACK), missing SEO fields
 * flagged amber, prev/next (buttons + ←/→) to walk the set, Esc / backdrop-click to close.
 * Save-as-you-type (debounced) via the parent's media-update call — no save button; a quiet
 * "saved" tick. Accessible: role=dialog + aria-modal, focus-trap, focus restore, reduced-motion.
 *
 * Reads/writes ONLY an item's own fields (alt/caption/metadata) through the parent `onSave`
 * (→ media-update, which merges). Never touches grouping/assign/cover/order/export.
 * `live` → read-only: opens to VIEW, every field disabled (media-update 403s live anyway).
 */

const DEBOUNCE_MS = 600;
const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),textarea:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function MediaDetailModal({
  item,
  index,
  total,
  live,
  onPrev,
  onNext,
  onClose,
  onSave,
}: {
  item: DetailMediaItem;
  index: number; // 0-based position in the set
  total: number;
  live: boolean;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  onSave: (id: string, patch: SavePatch) => Promise<void>;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Focus restore + body scroll-lock. Remember what was focused when the modal opened and
  // restore it on unmount. Lock the background scroll while open so the page behind doesn't
  // move (a scroll-behind jump). `html { scrollbar-gutter: stable }` (index.css) keeps the
  // scrollbar's gutter reserved, so hiding the body scrollbar causes NO width shift — which
  // is what produced the thin top line / layout jump on open (Slice 2b (3)).
  useEffect(() => {
    restoreRef.current = (document.activeElement as HTMLElement) ?? null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Focus the dialog so the focus-trap + Esc work immediately (no scroll jump).
    requestAnimationFrame(() => dialogRef.current?.focus({ preventScroll: true }));
    const pendingTimers = timers.current;
    return () => {
      document.body.style.overflow = prevOverflow;
      restoreRef.current?.focus?.();
      // flush any pending debounced saves' timers (the save itself already fired optimistically)
      pendingTimers.forEach((t) => clearTimeout(t));
      if (savedTimer.current) clearTimeout(savedTimer.current);
    };
  }, []);

  const score = scoreMedia(item as ScorableMedia);
  const missingKeys = new Set(score.missing.map((m) => m.key));

  // Debounced per-field save. `fieldId` keys the debounce so edits to different fields don't
  // cancel each other. Builds the patch, fires media-update via the parent, flips the tick.
  const save = useCallback(
    (fieldId: string, patch: SavePatch) => {
      if (live) return;
      const existing = timers.current.get(fieldId);
      if (existing) clearTimeout(existing);
      setSaveState('saving');
      timers.current.set(
        fieldId,
        setTimeout(() => {
          void onSave(item.id, patch)
            .then(() => {
              setSaveState('saved');
              if (savedTimer.current) clearTimeout(savedTimer.current);
              savedTimer.current = setTimeout(() => setSaveState('idle'), 1600);
            })
            .catch(() => setSaveState('idle'));
        }, DEBOUNCE_MS),
      );
    },
    [item.id, live, onSave],
  );

  // Keyboard: Esc closes; ←/→ walk the set (but NOT while typing in a field).
  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    const typing = /^(INPUT|TEXTAREA|SELECT)$/.test((e.target as HTMLElement).tagName);
    if (!typing && e.key === 'ArrowLeft' && index > 0) onPrev();
    else if (!typing && e.key === 'ArrowRight' && index < total - 1) onNext();
    else if (e.key === 'Tab') trapTab(e);
  }

  function trapTab(e: React.KeyboardEvent) {
    const root = dialogRef.current;
    if (!root) return;
    const nodes = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((n) => n.offsetParent !== null);
    if (nodes.length === 0) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    const active = document.activeElement as HTMLElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // Field sets. Metadata PACK: video = the full pack; image = title/category/tags (its health
  // set is alt·caption·tags, so tags must be fillable here too). alt/caption are row fields.
  const metaFields = item.kind === 'video' ? VIDEO_PACK : VIDEO_PACK.filter((f) => ['title', 'category', 'tags'].includes(f.key));
  const titleField = metaFields.find((f) => f.key === 'title');
  const restMeta = metaFields.filter((f) => f.key !== 'title');

  const metaSeed = (f: VideoPackField): string =>
    f.type === 'tags'
      ? serializeTags(item.metadata?.[f.key])
      : f.type === 'moments'
        ? serializeKeyMoments(item.metadata?.[f.key])
        : (item.metadata?.[f.key] as string | undefined) ?? '';

  // A metadata-field / row-field change → the shared patch builders (identical to the
  // spreadsheet's write), debounced per field. media-update merges; empty → key dropped.
  function onMeta(f: VideoPackField, raw: string) {
    save(`meta:${f.key}`, metaFieldPatch(item, f, raw));
  }
  function onRow(field: 'alt' | 'caption', raw: string) {
    save(field, rowFieldPatch(field, raw));
  }

  const isVideo = item.kind === 'video';
  const canPlay = isVideo && !!item.url; // the progressive MP4 (rule 18) once encoded
  const fname = (item.original_filename ?? '').split('/').pop() ?? '';

  // Slice 2c (3): render as a true top-level overlay via a portal to document.body — ABOVE
  // #root (z-index:1) and .spotlight. Rendered inside #root, the `fixed` backdrop sat in the
  // same stacking layer as the solid `bg-paper` <header>, which ghosted through the
  // translucent backdrop as a grey strip at the top. A body-level portal covers the whole
  // shell uniformly. Accessibility is unaffected: React events still bubble through the React
  // tree, and focus-trap / focus-restore / Esc / backdrop-close all key off the dialog ref +
  // the dialog's own onKeyDown, not the DOM mount location.
  return createPortal(
    // Backdrop — click to close (but not a click that started inside the dialog). FLAT house
    // rule (visual-cms.md §5a): a plain dimmed backdrop, no opacity/scale animation.
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* FLAT pop-up (visual-cms.md §5a): a plain surface + 1px border over the dimmed
          backdrop — NO neumorphic glow/shadow (not `.card`), no 3D/scale animation. */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Media details — ${titleField ? metaSeed(titleField) || fname : fname}`}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-line bg-paper outline-none"
      >
        {/* header: position · prev/next · save tick · close */}
        <div className="flex flex-none items-center gap-2 border-b border-line p-3">
          <span className="text-xs tabular-nums text-ink-2">{index + 1} / {total}</span>
          <div className="ml-1 flex items-center gap-1">
            <button type="button" onClick={onPrev} disabled={index === 0} className={`${btnGhostSm} disabled:opacity-30`} aria-label="Previous">←</button>
            <button type="button" onClick={onNext} disabled={index >= total - 1} className={`${btnGhostSm} disabled:opacity-30`} aria-label="Next">→</button>
          </div>
          <span className="ml-auto text-xs text-success" aria-live="polite">
            {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? '✓ Saved' : ''}
          </span>
          <button type="button" onClick={onClose} className={`${btnGhostSm}`} aria-label="Close">✕</button>
        </div>

        {/* body: media large + filename + health + fields */}
        <div className="flex-1 overflow-y-auto p-4" key={item.id}>
          <div className="relative grid aspect-[16/9] place-items-center overflow-hidden rounded bg-paper-2">
            {isVideo ? (
              canPlay ? (
                // Slice 2b (1): the video PLAYS — native controls from the progressive MP4
                // (item.url; built by construction, rule 18), poster as the still. Playback
                // is read-only (never writes) → fine on a live client.
                <video
                  key={item.url ?? item.id}
                  src={item.url ?? undefined}
                  poster={item.poster ?? undefined}
                  controls
                  playsInline
                  preload="metadata"
                  className="h-full w-full bg-black object-contain"
                />
              ) : (
                // Video not yet encoded to MP4 (no url yet) → show the poster + an honest note.
                <>
                  {item.poster ? (
                    <img src={item.poster} alt="" className="h-full w-full object-contain" />
                  ) : (
                    <span className="text-xs text-ink-2">no preview</span>
                  )}
                  <span className="pointer-events-none absolute bottom-2 rounded bg-black/60 px-2 py-0.5 text-[11px] text-white">
                    video still processing…
                  </span>
                </>
              )
            ) : item.url ? (
              <img src={item.url} alt="" className="h-full w-full object-contain" />
            ) : (
              <span className="text-xs text-ink-2">no preview</span>
            )}
          </div>
          {fname && <p className="mt-2 truncate font-mono text-[11px] text-ink-2" title={fname}>{fname}</p>}

          {/* health bar — pct + the plain-English "still needs …" line */}
          <div className="mt-3 flex items-center gap-2">
            <span className="h-[7px] flex-1 overflow-hidden rounded-full bg-line">
              <span className={`block h-full ${score.missing.length ? 'bg-warning-bright' : 'bg-success'}`} style={{ width: `${score.pct}%` }} />
            </span>
            <span className="whitespace-nowrap text-xs font-semibold tabular-nums text-ink-2">
              {score.filled}/{score.total}
              {score.missing.length > 0 && <span className="ml-1 font-normal text-warning">· still needs {score.missing.map((m) => m.label.toLowerCase()).join(', ')}</span>}
            </span>
          </div>

          {live && <p className="mt-3 rounded border border-warning-bright bg-warning-bright/10 p-2 text-xs text-warning">🔒 Site is live/protected — fields are read-only.</p>}

          {/* fields: title (big) → alt → caption → the rest of the metadata PACK */}
          <div className="mt-4 space-y-3">
            {titleField && (
              <Field label="Title" flagged={missingKeys.has('title' as never)}>
                <input defaultValue={metaSeed(titleField)} disabled={live} onChange={(e) => onMeta(titleField, e.target.value)} className={inputCls(false, live)} />
              </Field>
            )}
            <Field label="Alt text" seo flagged={missingKeys.has('alt')}>
              <input defaultValue={item.alt} disabled={live} onChange={(e) => onRow('alt', e.target.value)} className={inputCls(missingKeys.has('alt'), live)} />
            </Field>
            <Field label="Caption" flagged={missingKeys.has('caption')}>
              <input defaultValue={item.caption ?? ''} disabled={live} onChange={(e) => onRow('caption', e.target.value)} className={inputCls(missingKeys.has('caption'), live)} />
            </Field>
            {restMeta.map((f) => {
              const flagged = missingKeys.has(f.key as never);
              return (
                <Field key={f.key} label={f.label} seo={!!f.priority} flagged={flagged} help={f.help}>
                  {f.type === 'textarea' || f.type === 'moments' ? (
                    <textarea
                      defaultValue={metaSeed(f)}
                      disabled={live}
                      rows={f.type === 'moments' ? 3 : 3}
                      placeholder={f.placeholder}
                      onChange={(e) => onMeta(f, e.target.value)}
                      className={inputCls(flagged, live)}
                    />
                  ) : (
                    <input
                      defaultValue={metaSeed(f)}
                      disabled={live}
                      placeholder={f.placeholder}
                      onChange={(e) => onMeta(f, e.target.value)}
                      className={inputCls(flagged, live)}
                    />
                  )}
                </Field>
              );
            })}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function inputCls(flagged: boolean, live: boolean): string {
  return `w-full rounded border px-2 py-1.5 text-sm ${flagged ? 'border-warning-bright bg-warning-bright/5' : 'border-line bg-paper'} ${live ? 'opacity-60' : ''}`;
}

function Field({ label, seo, flagged, help, children }: { label: string; seo?: boolean; flagged?: boolean; help?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 flex items-center gap-2 text-xs font-medium text-ink-2">
        {label}
        {seo && <span className="rounded-full bg-accent-subtle px-1.5 py-0.5 text-[10px] font-semibold text-accent-ink">SEO</span>}
        {flagged && <span className="ml-auto rounded-full bg-warning-bright px-1.5 py-0.5 text-[10px] font-semibold text-ink">missing</span>}
      </span>
      {children}
      {help && <span className="mt-0.5 block text-[10px] text-ink-2">{help}</span>}
    </label>
  );
}
